import { isBackgroundReportIncomplete } from "../../queryHelpers";
import { CategoryResult } from "../helpers/categoryProcessors";

/**
 * Determine the aggregate status from a list of statuses
 * If any are "fail", the result is "fail"
 * If any are "warn", the result is "warn"
 * Otherwise "pass"
 */
export function determineAggregateStatus(statuses) {
  if (statuses.includes("fail")) return "fail";
  if (statuses.includes("warn")) return "warn";
  return "pass";
}

/**
 * Return a standard "Pending" status for use when Lumen is still calculating
 */
export function getPendingStatus(): CategoryResult {
  return {
    status: "pending",
    label: "Pending",
  };
}

/**
 * Create a default category data structure with optional additional properties
 */
export function createDefaultCategoryData(
  additionalProps = {}
): CategoryResult {
  return {
    status: "pass",
    label: "",
    ...additionalProps,
  };
}

/**
 * Find outcomes relevant to a specific category using keywords
 */
export function findRelevantOutcomes(lumenRun, categoryKeywords) {
  if (!lumenRun?.outcomes || !Array.isArray(lumenRun.outcomes)) {
    return [];
  }

  // Convert keywords to lowercase for case-insensitive matching
  const normalizedKeywords = Array.isArray(categoryKeywords)
    ? categoryKeywords.map((k) => k.toLowerCase())
    : [categoryKeywords.toLowerCase()];

  // Find outcomes that match the keywords in their name or scoreLabel
  return lumenRun.outcomes.filter((outcome) => {
    const nameMatch =
      outcome.name &&
      normalizedKeywords.some((keyword) =>
        outcome.name.toLowerCase().includes(keyword)
      );

    const labelMatch =
      outcome.scoreLabel &&
      normalizedKeywords.some((keyword) =>
        outcome.scoreLabel.toLowerCase().includes(keyword)
      );

    return nameMatch || labelMatch;
  });
}

/**
 * Extract status from a collection of outcomes
 */
export function getStatusFromOutcomes(outcomes) {
  if (!outcomes || outcomes.length === 0) {
    return null;
  }

  // Extract status from scoreLabelColor
  const statuses = outcomes.map((outcome) => outcome.scoreLabelColor || "pass");
  return determineAggregateStatus(statuses);
}

/**
 * Get detailed explanation from applicant or application evaluation data based on category
 * This extracts more specific information about why a condition failed
 */
export function getOutcomeDetailsFromApplicantData(lumenResults, categoryName) {
  if (!lumenResults) {
    return undefined;
  }

  // Normalize category name for matching
  const normalizedCategory = categoryName.toLowerCase();

  // Skip empty arrays
  if (lumenResults.length === 0) {
    return undefined;
  }

  // Collect ALL matching detailed explanations across applicants and formulas
  const allDetails = lumenResults.flatMap((resultData) => {
    if (
      !resultData?.evaluation?.triggeredOutcomes ||
      !Array.isArray(resultData.evaluation.triggeredOutcomes)
    ) {
      return [];
    }

    // Collect explanations from all matching formulas for this applicant
    return resultData.evaluation.triggeredOutcomes.flatMap((formulaName) => {
      // Skip if formula doesn't include the category name
      if (
        typeof formulaName !== "string" ||
        !formulaName.toLowerCase().includes(normalizedCategory)
      ) {
        return [];
      }

      // If we found a matching outcome, look for corresponding history item
      if (
        !resultData.evaluation.history ||
        !Array.isArray(resultData.evaluation.history)
      ) {
        return [];
      }

      const historyItem = resultData.evaluation.history.find(
        (item) =>
          item.formula && formulaName && item.formula.includes(formulaName)
      );

      if (!historyItem?.explanation?.output) {
        return [];
      }

      const { output } = historyItem.explanation;

      // Parse output which could be a string or object
      if (typeof output === "string") {
        // Try to parse string format: "Title: The title\n\nDescription: The description"
        const titleMatch = output.match(/Title:\s*(.*?)(?:\n\n|\n|$)/);
        const descriptionMatch = output.match(
          /Description:\s*(.*?)(?:\n\n|\n|$)/
        );

        return [
          {
            title: titleMatch ? titleMatch[1] : "Issue Found",
            description: descriptionMatch ? descriptionMatch[1] : output,
          },
        ];
      }
      if (typeof output === "object") {
        // Output is already an object, extract title and description
        return [
          {
            title: output.title || "Issue Found",
            description: output.description || "No details available",
          },
        ];
      }

      return [];
    });
  });

  // Return the array of all collected details or undefined if none found
  return allDetails.length > 0 ? allDetails : undefined;
}

/**
 * Helper function to get category status from lumen outcomes
 * This centralizes the logic for finding relevant outcomes and extracting status and details
 */
export function getCategoryStatusFromOutcomes(lumenRun, categoryKeywords) {
  if (!lumenRun) {
    return getPendingStatus();
  }

  // Find relevant outcomes based on keywords
  const relevantOutcomes = findRelevantOutcomes(lumenRun, categoryKeywords);
  const status = getStatusFromOutcomes(relevantOutcomes);

  // Try to get detailed explanation from lumenRun.results directly
  const normalizedKeyword = Array.isArray(categoryKeywords)
    ? categoryKeywords[0].toLowerCase()
    : categoryKeywords.toLowerCase();

  // Get detailed explanation if available
  // We use lumenRun.results directly now instead of a separate parameter
  const detailedExplanation = getOutcomeDetailsFromApplicantData(
    lumenRun.results,
    normalizedKeyword
  );

  return {
    status,
    details: detailedExplanation,
  };
}

/**
 * Get details for an incomplete background report
 * @param {Object} lumenRun - The lumen run object
 * @returns {Object} Object containing status, details, and label
 */
export function getIncompleteBackgroundDetails(lumenRun) {
  // Skip if there's no incomplete background report
  if (!isBackgroundReportIncomplete(lumenRun) || !lumenRun?.results) {
    return { status: null, details: null, label: null };
  }

  // Find the background report outcome and get its status
  const backgroundOutcome = lumenRun.outcomes.find(
    (outcome) =>
      outcome.name &&
      outcome.name.toLowerCase().includes("background-reports-run")
  );

  // Get status from the outcome
  const outcomeStatus = backgroundOutcome?.scoreLabelColor || "warn";

  // Look for a single match in any of the results
  let matchDetail = null;

  // Find the first matching history item using array methods instead of a for loop with continue
  const results = lumenRun.results || [];

  // Use find to get the first result with a matching background formula and history item
  results.find((resultData) => {
    // Skip results without required data
    if (
      !resultData?.evaluation?.triggeredOutcomes ||
      !resultData?.evaluation?.history
    ) {
      return false;
    }

    // Find background-related triggered outcome
    const backgroundFormula = resultData.evaluation.triggeredOutcomes.find(
      (formula) =>
        typeof formula === "string" &&
        formula.toLowerCase().includes("background")
    );

    if (!backgroundFormula) {
      return false;
    }

    // Find corresponding history item
    const historyItem = resultData.evaluation.history.find(
      (item) =>
        item.formula &&
        backgroundFormula &&
        item.formula.includes(backgroundFormula)
    );

    if (!historyItem?.explanation?.output) {
      return false;
    }

    // If we got here, we found a match - extract the details
    const { output } = historyItem.explanation;

    if (typeof output === "object") {
      matchDetail = {
        title: output.title || "SSN Verification Issue",
        description:
          output.description ||
          "There was an issue verifying the applicant's SSN",
        status: outcomeStatus,
      };
      return true; // Found a match
    }

    return false;
  });

  // Return results based on whether we found a match
  return {
    status: matchDetail ? outcomeStatus : null,
    details: matchDetail ? [matchDetail] : null,
    label: matchDetail ? "No SSN" : null,
  };
}
